/* The theatre's right column — KANON `Cwiczenia` 17.09.2026, `2-karta.html#e`'s second `.k-tcol`.
 *
 * Six blocks with a hairline between them, in a column that scrolls inside itself. `min-height: 0`
 * is what makes the scroll real: a flex item's default `min-height: auto` refuses to shrink below
 * its content, so without it the column grows to the height of the longest movement's instructions
 * and the PANEL scrolls instead — which on a fixed-height lightbox means it simply clips.
 *
 * Nothing here waits, so nothing here needs a reserve of its own: the muscles, the equipment, the
 * metrics, the prose and the steps all arrive with the catalogue. The one late answer is inside
 * „Materiały", and `MaterialsBlock.module.css` reserves it there.
 *
 * Every text leaf states its line-height. */

.rail {
  display: flex;
  flex-direction: column;
  min-height: 0;

  /* The right inset is the specimen's, for the scrollbar. The BOTTOM one is the fit matrix's: with
   * the rail scrolled to its end the last instruction sat flush against the scroller's edge and its
   * final fraction of a line was clipped by the `overflow` — measured at 1280 x 620, where
   * `toBeInViewport` put it at 0.983 of itself. A scroller whose last line ends exactly on its own
   * edge also READS as cut, whether or not anything is actually missing. */
  padding-right: var(--space-6px);
  padding-bottom: var(--space-2);
  overflow-y: auto;
  overscroll-behavior: contain;
}

/* The column is a tab stop, because it is a scroller and a keyboard has no other way into it. The
 * ring is INSET for the same reason the table's rows carry an inset one: an outset ring on a box
 * flush against the panel's own edge would be drawn half outside it. */
.rail:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--outline-inset);
}

.block {
  flex: none;
}

.title {
  margin: var(--space-0) var(--space-0) var(--space-10px);
  font-size: var(--fs-body);
  font-weight: var(--fw-bold);
  line-height: var(--lh-display);
  letter-spacing: var(--ls-heading);
  color: var(--text);
}

/* The specimen's `.mb-rule` at its `.k-rule` spacing. An `<hr>` rather than a bordered box, because
 * it is a thematic break between two sections and that is what the element means. */
.rule {
  flex: none;
  height: 1px;
  margin: var(--space-19px) var(--space-0);
  background: var(--border);
  border: none;
}

/* ── Partie mięśniowe ───────────────────────────────────────────────────────────────────────── */

.figures {
  display: flex;
  margin-bottom: var(--space-3);
}

.muscleRow {
  display: flex;
  gap: var(--space-10px);
  align-items: baseline;
  margin-top: var(--space-2);
}

/* A fixed column, so „Główne" and „Pomocnicze" line their pills up on one left edge rather than each
 * row starting wherever its own word ends. */
.muscleLabel {
  flex: none;
  width: var(--w-ex-theatre-muscle-label);
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  line-height: var(--lh-normal);
  color: var(--text-3);
}

.pills {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-6px);
  min-width: 0;
}

.pill {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: var(--space-0) var(--space-10px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  white-space: nowrap;
  color: var(--text);
  background: var(--bg-chip);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-8px);
}

/* The weaker treatment for a secondary group — a different WEIGHT of the same fact, not a different
 * colour, so the distinction survives a monochrome reading. The words „Pomocnicze" beside them are
 * what actually says it. */
.pillSecondary {
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  background: transparent;
}

/* The rail is wider than the preview column, so four tracks would stretch a 72px product shot to
 * 112. Six lands it where the canon draws it. See `EquipmentTiles.module.css`. */
.equipment {
  --ex-equip-columns: 6;
}

/* ── Metryki ────────────────────────────────────────────────────────────────────────────────── */

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-6px);
}

.chip {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: var(--space-0) var(--space-9px);
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-7px);
  font-variant-numeric: tabular-nums;
}

/* „Tylko wykonanie" stands in the chips' own row rather than as a grey aside, because it IS the
 * answer to „co mierzymy" and not the absence of one. */
.chipAbsence {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: var(--space-0) var(--space-9px);
  margin: var(--space-0);
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-7px);
}

/* ── Opis ───────────────────────────────────────────────────────────────────────────────────── */

.prose {
  margin: var(--space-0);
  font-size: var(--fs-exercise);
  font-weight: var(--fw-regular);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

.proseAbsent {
  margin: var(--space-0);
  font-size: var(--fs-exercise);
  font-weight: var(--fw-regular);
  line-height: var(--lh-relaxed);
  color: var(--text-3);
}

/* ── Jak wykonać ────────────────────────────────────────────────────────────────────────────── */

/* The numbers are the list's own counter rather than text in each item, so they can never be out of
 * step with what they number. `list-style: none` because the counter is drawn on a chip. */
.steps {
  display: flex;
  flex-direction: column;
  gap: var(--space-10px);
  margin: var(--space-0);
  padding: var(--space-0);
  list-style: none;
  counter-reset: step;
}

.step {
  display: grid;
  grid-template-columns: var(--h-ex-step-mark) 1fr;
  gap: var(--space-10px);
  font-size: var(--fs-exercise);
  font-weight: var(--fw-regular);
  line-height: var(--lh-normal);
  color: var(--text);
}

.step::before {
  display: grid;
  place-items: center;
  width: var(--h-ex-step-mark);
  height: var(--h-ex-step-mark);
  font-size: var(--fs-caption);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-none);
  color: var(--text-2);
  content: counter(step);
  counter-increment: step;
  background: var(--bg-chip);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-xs);
  font-variant-numeric: tabular-nums;
}

/* Every block's own absence sentence, in the one treatment. */
.absence {
  margin: var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-3);
}
